import { useCountdown } from "hooks/timers";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useEffect, useRef, useState } from "react";
import { getFridgeId } from "utils/browser/localStorage";
import { logToSentry } from "utils/logger";
import { getShortenedFridgeId } from "utils/utils";
import { BasicModal } from "./BasicModal";
import { ErrorModalContent } from "./modalsContents/ErrorModalContent";

type ConnectionErrorModalProps = {
	appConnected: boolean;
	isSubscriptionBroken?: boolean;
	isZombie?: boolean;
};

export const ConnectionErrorModal = ({ appConnected, isSubscriptionBroken, isZombie }: ConnectionErrorModalProps) => {
	const fridgeId = getFridgeId();
	const shortenedFridgeId = getShortenedFridgeId(fridgeId);
	const [isOpen, setIsOpen] = useState(() => {
		if (isZombie) {
			return true;
		}
		if (isSubscriptionBroken) {
			return false;
		}
		return !appConnected;
	});
	const { countdown } = useCountdown(15);
	const { trackTabletErrorModal } = useMixpanelTrackers();
	const trackRef = useRef(trackTabletErrorModal);
	trackRef.current = trackTabletErrorModal;
	const wasOpenRef = useRef(false);

	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			wasOpenRef.current = true;
			trackRef.current("open");
			logToSentry(`Error modal displayed on fridge: ${fridgeId}`, "error");
		}
		return () => {
			if (wasOpenRef.current) {
				wasOpenRef.current = false;
				trackRef.current("close");
			}
		};
	}, [isOpen, fridgeId]);

	// Keep the modal in sync with the connection, both directions. Before 1.14.0 the modal rendered
	// `open={!appConnected}`, so when the Google-DNS ping flipped appConnected back to true the modal
	// closed itself. The JID-1131 refactor replaced that with a sticky isOpen that was only ever set
	// to true, so the modal could no longer dismiss on recovery — it relied on being unmounted. Restore
	// the reactive close: hide the moment we reconnect; otherwise show (after the grace countdown when
	// the trigger is a dropped subscription rather than a hard disconnect).
	// A zombie (stream dead, network up) must also show immediately and dismiss when the stream
	// recovers. The isZombie flag is derived in App.tsx using episode history so that the zombie
	// case does not interfere with the dismiss-on-DNS-recovery path for real outages.
	useEffect(() => {
		if (appConnected && !isZombie) {
			// Fully recovered network (and not a zombie) → dismiss, even before the first frame.
			// This is the dismiss-on-DNS-recovery behavior that must be preserved for real outages.
			setIsOpen(false);
		} else if (isZombie || !isSubscriptionBroken || countdown === 0) {
			// Zombie shows immediately (detection already absorbed the delay); a hard disconnect shows
			// immediately; a dropped-subscription outage shows after the grace countdown.
			setIsOpen(true);
		}
	}, [appConnected, isSubscriptionBroken, isZombie, countdown]);

	return (
		<BasicModal
			open={isOpen}
			fullHeight={true}
			closable={false}
			modalContent={<ErrorModalContent fridgeId={shortenedFridgeId} />}
		/>
	);
};
